import type React from "react"

import { useActiveOrganization, useAuthentication } from "@/contexts"
import { useFlags } from "@/services/feature"
import type { FeatureFlagName } from "@/services/feature"

import { Page404 } from "../../nav/page404"

interface FeatureGuardProps {
  /**
   * When true, an admin or admin reader sees the feature even if the flag is off.
   */
  allowAdmin?: boolean
  /**
   * The children to render if the feature is enabled
   */
  children: React.ReactNode
  /**
   * If the feature is enabled the children will be rendered unless invert is true
   */
  feature: FeatureFlagName
  /**
   * If true the children will be rendered when the feature is off and a 404 will be rendered when the feature is on
   */
  invert?: boolean
}

/**
 * @description A guard that displays or hides the feature
 */
export const FeatureGuard: React.FC<FeatureGuardProps> = ({
  allowAdmin = false,
  children,
  feature,
  invert,
}) => {
  const { activeOrganization } = useActiveOrganization()
  const { isAdminUser } = useAuthentication()
  const { flagsIsFetched, ...flags } = useFlags()

  if (!activeOrganization) {
    return null
  }

  if (allowAdmin && isAdminUser && !invert) {
    return children
  }

  // Wait until flags are resolved before deciding access.
  // This prevents a brief 404 flash on hard refresh while flags are loading.
  if (!flagsIsFetched) {
    return null
  }

  const isFeatureEnabled = flags[feature]

  const isAllowed = invert ? !isFeatureEnabled : isFeatureEnabled

  if (isAllowed) {
    return children
  }

  return <Page404 />
}
